import { isBlackOpening, loadBlackCourse, loadedBlackCourse } from "./blackCourses.ts";
import { isWhiteOpening, loadWhiteCourse, whiteCourseLoaded } from "./whiteCourses.ts";
import { Chess } from "@vendor/chess.js";
import type { ChessConstructor } from "../chess/chessTypes.ts";
import { COURSE_BUILDERS } from "./catalog.ts";
import { manifestByOpeningId, manifestBySlug } from "./manifest.ts";
import type { LessonCourse } from "../lessons/schema.ts";
import type { OpeningId } from "../lessons/ids.ts";
import { loadCourseCopy, loadedOpeningRecord } from "./catalogCopy.ts";

export type CourseSummary = {
  openingId: OpeningId;
  side: LessonCourse["side"];
  slug: string;
  title: string;
  eco: string;
  tagline: string;
  /** What the course trains, in a few words ("Pawn tension and positional planning"). */
  focus: string;
  /** White's first move in the course's main line. */
  firstMove: string;
  limitedCoverage: boolean;
  level: LessonCourse["level"];
  reviewStatus: LessonCourse["reviewStatus"];
};

// Building a course graph replays every authored line, so each course is built
// only when something needs it, and catalog pages never build one.
const built = new Map<OpeningId, LessonCourse>();

export function courseById(id: OpeningId): LessonCourse {
  let course = built.get(id);
  if (!course) {
    course = COURSE_BUILDERS[id](Chess as unknown as ChessConstructor);
    built.set(id, course);
  }
  return course;
}

/** The course if its body has been downloaded; null otherwise. */
export function loadedCourseById(id: OpeningId): LessonCourse | null {
  if (isWhiteOpening(id) && !whiteCourseLoaded(id)) return null;
  if (isBlackOpening(id) && !loadedBlackCourse(id)) return null;
  return courseById(id);
}

export function getCourseCatalog(): Record<OpeningId, LessonCourse> {
  return Object.fromEntries(
    (Object.keys(COURSE_BUILDERS) as OpeningId[]).map((id) => [id, courseById(id)])
  ) as Record<OpeningId, LessonCourse>;
}

export function listCourses(): CourseSummary[] {
  return (Object.keys(COURSE_BUILDERS) as OpeningId[]).flatMap((openingId) => {
    const manifest = manifestByOpeningId(openingId);
    if (!manifest) return [];
    return [
      {
        openingId,
        side: isBlackOpening(openingId) ? ("black" as const) : ("white" as const),
        slug: manifest.slug,
        title: manifest.title,
        eco: manifest.catalogEco,
        tagline: manifest.tagline,
        focus: manifest.focus,
        firstMove: manifest.firstMove,
        limitedCoverage: false,
        level: manifest.level,
        reviewStatus: manifest.reviewStatus,
      },
    ];
  });
}

/** The course for a slug. Its body must already be loaded (see loadCourseBySlug). */
export function courseBySlug(slug: string): LessonCourse | null {
  const manifest = manifestBySlug(slug);
  return manifest ? courseById(manifest.openingId) : null;
}

/** Download what a course needs (its body and copy), then build it. */
export async function loadCourseBySlug(slug: string): Promise<LessonCourse | null> {
  const manifest = manifestBySlug(slug);
  if (!manifest) return null;
  const id = manifest.openingId;
  await Promise.all([
    loadCourseCopy(),
    isBlackOpening(id) ? loadBlackCourse(id) : null,
    isWhiteOpening(id) ? loadWhiteCourse(id) : null,
  ]);
  return courseById(id);
}

export function summaryBySlug(slug: string): CourseSummary | null {
  return listCourses().find((c) => c.slug === slug) || null;
}

/** The course's legacy opening record; loadCourseBySlug downloads it. */
export function openingRecord(openingId: string) {
  return loadedOpeningRecord(openingId);
}
